import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '@/lib/query-keys';

/**
 * Archive or un-archive an inbox thread. Archived threads leave Needs Reply /
 * In Conversation / Cold and stay under All until the customer writes again.
 * Invalidates the whole `['conversations']` key: the list AND the tab counts
 * move.
 */
export const useArchiveConversation = (options?: {
  /** Bulk archive raises one summary toast instead of one per thread. */
  showToasts?: boolean;
}) => {
  const queryClient = useQueryClient();
  const showToasts = options?.showToasts ?? true;

  const mutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiClient.post<{ id: string; archivedAt: string | null }>(
        `conversations/${id}/${archived ? 'archive' : 'unarchive'}`
      ),
    onSuccess: (_data, { archived }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all(),
      });
      if (showToasts) {
        toast.success(
          archived ? 'Conversation archived' : 'Conversation unarchived'
        );
      }
    },
    onError: (error: Error, { archived }) => {
      if (showToasts) {
        toast.error(
          error.message ||
            `Failed to ${archived ? 'archive' : 'unarchive'} conversation`
        );
      }
    },
  });

  return {
    setArchived: mutation.mutate,
    setArchivedAsync: mutation.mutateAsync,
    isArchiving: mutation.isPending,
  };
};
